<script setup lang="ts">
import { computed } from 'vue';
import { HeaderCarousel } from '~/components/header-carousel';
import { useOrganizationStore } from '~/store/organization';
import { DashboardSectionType, UserDashboardConfigDto } from '~/api/dashboard';
import { DashboardSearch } from '~/components/dashboard-search';
import { HubCard } from './_components/hub-card';
import { ModuleCard } from './_components/module-card';
import { SpotlightCard } from './_components/spotlight-card';
import { NewDashboardModal } from './_components/new-dashboard-modal';

type Props = {
  config: UserDashboardConfigDto;
};

const props = defineProps<Props>();

const { organization } = storeToRefs(useOrganizationStore());

const sortedSections = computed(() =>
  [...props.config.sections].sort((a, b) => a.position - b.position),
);

const SectionType = DashboardSectionType;
</script>

<template>
  <div class="p-6">
    <ClientOnly>
      <NewDashboardModal />
    </ClientOnly>

    <div
      class="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between"
    >
      <h1 class="text-2xl font-bold">
        {{ $t('user-dashboard.welcome', { name: organization?.name ?? '' }) }}
      </h1>

      <div class="w-full sm:max-w-md">
        <DashboardSearch />
      </div>
    </div>

    <HeaderCarousel
      v-if="config.headerImages.length > 0"
      :images="config.headerImages"
      :auto-rotate="config.isAutoAdvanceEnabled"
      :interval-seconds="config.rotationIntervalSeconds"
      class="mb-6"
    />

    <div class="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
      <template v-for="section of sortedSections" :key="section.position">
        <HubCard
          v-if="section.type === SectionType.HUB && section.hub"
          :hub="section.hub"
        />
        <ModuleCard
          v-else-if="section.type === SectionType.MODULE && section.module"
          :module="section.module"
        />
        <SpotlightCard
          v-else-if="
            section.type === SectionType.SPOTLIGHT && section.spotlight
          "
          :spotlight="section.spotlight"
        />
      </template>
    </div>
  </div>
</template>
